import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, X, Shirt, Check,
  Sun, Cloud, Snowflake, CloudRain, Wind, Sparkles, Star,
  Trash2, Eye, Clock, Tag, Loader2, Image as ImageIcon,
  ChevronDown, Footprints, ShoppingBag, Pencil, Save, Bookmark,
  ArrowUp, ArrowDown
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
// Dialog kept for item picker only — plan outfit is inline
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/ui/use-toast';
import { getApiUrl } from '../utils';

const API_URL = getApiUrl();

const OCCASIONS = [
  { value: 'work', label: 'Work', emoji: '💼' },
  { value: 'casual', label: 'Casual', emoji: '😎' },
  { value: 'date', label: 'Date Night', emoji: '❤️' },
  { value: 'event', label: 'Event', emoji: '🎉' },
  { value: 'gym', label: 'Gym', emoji: '💪' },
  { value: 'brunch', label: 'Brunch', emoji: '🥂' },
  { value: 'travel', label: 'Travel', emoji: '✈️' },
  { value: 'lounge', label: 'Lounge', emoji: '🛋️' },
];

const WEATHER_OPTIONS = [
  { value: 'sunny', label: 'Sunny', icon: Sun, color: 'text-yellow-500' },
  { value: 'cloudy', label: 'Cloudy', icon: Cloud, color: 'text-gray-400' },
  { value: 'rainy', label: 'Rainy', icon: CloudRain, color: 'text-stone-400' },
  { value: 'cold', label: 'Cold', icon: Snowflake, color: 'text-stone-300' },
  { value: 'windy', label: 'Windy', icon: Wind, color: 'text-stone-500' },
];

const MOODS = ['😊', '😍', '💪', '🤩', '😌', '🔥', '✨', '💅'];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function OutfitCalendar() {
  const { toast } = useToast();
  const token = localStorage.getItem('token');

  // View state
  const [viewMode, setViewMode] = useState('month'); // 'today', 'week', 'month', or 'year'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const weekScrollRef = useRef(null);
  const touchStartX = useRef(0);

  // Data
  const [calendarEntries, setCalendarEntries] = useState({});
  const [wardrobeItems, setWardrobeItems] = useState([]);
  const [savedOutfits, setSavedOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  const [markingWornId, setMarkingWornId] = useState(null);
  const plannerRef = useRef(null);

  // Plan panel (inline, no dialog)
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [whiteboardEntry, setWhiteboardEntry] = useState(null);
  const whiteboardCanvasRef = useRef(null);
  const dragFlagRef = useRef(false);
  const whiteboardItemKeysRef = useRef([]);
  const [savingWhiteboard, setSavingWhiteboard] = useState(false);
  const [showSaveNameDialog, setShowSaveNameDialog] = useState(false);
  const [saveOutfitName, setSaveOutfitName] = useState('');
  const [itemScales, setItemScales] = useState({});
  const [itemLayers, setItemLayers] = useState({});
  const [selectedWhiteboardItem, setSelectedWhiteboardItem] = useState(null);
  const [planForm, setPlanForm] = useState({
    title: '', occasion: '', notes: '', weather: '', mood: '',
    top_item_id: null, bottom_item_id: null, dress_item_id: null,
    outerwear_item_id: null, shoes_item_id: null, bag_item_id: null,
    sportswear_item_id: null, swimwear_item_id: null, accessory_ids: [],
    outfit_combination_id: null, custom_image_url: ''
  });

  // Item picker
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [pickingSlot, setPickingSlot] = useState(null);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemFilterCategory, setItemFilterCategory] = useState('all');

  // Helper: get week range from date
  const getWeekRange = useCallback((date) => {
    const d = new Date(date);
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday, end: sunday };
  }, []);

  // Helper: get month range
  const getMonthRange = useCallback((date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { start, end };
  }, []);

  const formatDateKey = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      let range;
      if (viewMode === 'today') {
        range = { start: currentDate, end: currentDate };
      } else if (viewMode === 'week') {
        range = getWeekRange(currentDate);
      } else if (viewMode === 'year') {
        const year = currentDate.getFullYear();
        range = { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
      } else {
        range = getMonthRange(currentDate);
      }
      const startStr = formatDateKey(range.start);
      const endStr = formatDateKey(range.end);

      const res = await axios.get(`${API_URL}/outfit-calendar?startDate=${startStr}&endDate=${endStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const entries = {};
      (res.data.entries || []).forEach(entry => {
        // Handle both plain date strings ('2026-03-18') and ISO timestamps
        const dateKey = entry.date && entry.date.includes('T')
          ? formatDateKey(new Date(entry.date))
          : entry.date;
        if (!entries[dateKey]) entries[dateKey] = [];
        entries[dateKey].push(entry);
      });
      setCalendarEntries(entries);
    } catch (err) {
      console.error('❌ Failed to load calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [token, currentDate, viewMode, getWeekRange, getMonthRange]);

  // Fetch wardrobe items
  const fetchWardrobeItems = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/real-wardrobe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWardrobeItems(res.data.items || []);
    } catch (err) {
      console.error('❌ Failed to load wardrobe:', err);
    }
  }, [token]);

  // Fetch saved outfits
  const fetchSavedOutfits = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/real-wardrobe/outfits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedOutfits(res.data.outfits || []);
    } catch (err) {
      console.error('❌ Failed to load outfits:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  useEffect(() => {
    fetchWardrobeItems();
    fetchSavedOutfits();
  }, [fetchWardrobeItems, fetchSavedOutfits]);

  // Check for calendar inspiration from URL query params (from Saved Items page)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const title = searchParams.get('title');
    const image = searchParams.get('image');
    if (!title && !image) return;
    const brand = searchParams.get('brand') || '';
    // Clear URL params so they don't persist on refresh
    setSearchParams({}, { replace: true });
    // Auto-open the plan dialog for today with pre-filled data
    const today = new Date();
    setSelectedDate(today);
    setPlanForm(prev => ({
      ...prev,
      title: title || '',
      occasion: '',
      notes: `Inspired by: ${brand} ${title || ''}`.trim(),
      custom_image_url: image || '',
    }));
    setShowPlanDialog(true);
    // Scroll to planner after a short delay
    setTimeout(() => plannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate
  const navigatePrev = () => {
    const d = new Date(currentDate);
    if (viewMode === 'today') d.setDate(d.getDate() - 1);
    else if (viewMode === 'week') d.setDate(d.getDate() - 7);
    else if (viewMode === 'year') d.setFullYear(d.getFullYear() - 1);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };

  const navigateNext = () => {
    const d = new Date(currentDate);
    if (viewMode === 'today') d.setDate(d.getDate() + 1);
    else if (viewMode === 'week') d.setDate(d.getDate() + 7);
    else if (viewMode === 'year') d.setFullYear(d.getFullYear() + 1);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setViewMode('today');
  };

  // Open plan dialog for a date
  const openPlanDialog = (date, entryToEdit = null) => {
    setSelectedDate(date);

    // Ensure wardrobe items are loaded (re-fetch if empty or editing)
    if (wardrobeItems.length === 0 || entryToEdit) {
      fetchWardrobeItems();
    }

    if (entryToEdit) {
      setEditingEntryId(entryToEdit.id);
      setPlanForm({
        title: entryToEdit.title || '',
        occasion: entryToEdit.occasion || '',
        notes: entryToEdit.notes || '',
        weather: entryToEdit.weather || '',
        mood: entryToEdit.mood || '',
        top_item_id: entryToEdit.top_item_id,
        bottom_item_id: entryToEdit.bottom_item_id,
        dress_item_id: entryToEdit.dress_item_id,
        outerwear_item_id: entryToEdit.outerwear_item_id,
        shoes_item_id: entryToEdit.shoes_item_id,
        bag_item_id: entryToEdit.bag_item_id,
        sportswear_item_id: entryToEdit.sportswear_item_id,
        swimwear_item_id: entryToEdit.swimwear_item_id,
        accessory_ids: entryToEdit.accessory_ids || [],
        outfit_combination_id: entryToEdit.outfit_combination_id,
        custom_image_url: entryToEdit.custom_image_url || ''
      });
    } else {
      setEditingEntryId(null);
      setPlanForm({
        title: '', occasion: '', notes: '', weather: '', mood: '',
        top_item_id: null, bottom_item_id: null, dress_item_id: null,
        outerwear_item_id: null, shoes_item_id: null, bag_item_id: null,
        sportswear_item_id: null, swimwear_item_id: null, accessory_ids: [],
        outfit_combination_id: null, custom_image_url: ''
      });
    }
    setShowPlanDialog(true);
    // Scroll to inline planner after render
    setTimeout(() => plannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };

  // Save plan
  const savePlan = async () => {
    if (!selectedDate) return;
    setSaving(true);
    try {
      if (editingEntryId) {
        await axios.put(`${API_URL}/outfit-calendar/${editingEntryId}`, {
          ...planForm
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/outfit-calendar`, {
          date: formatDateKey(selectedDate),
          ...planForm
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast({ title: '✅ Outfit planned!', description: `Outfit saved for ${selectedDate.toLocaleDateString()}` });
      setShowPlanDialog(false);
      setSelectedDate(null);
      setEditingEntryId(null);
      fetchCalendarData();
    } catch (err) {
      console.error('❌ Failed to save plan:', err);
      toast({ title: '❌ Error', description: 'Failed to save outfit plan', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Delete entry
  const deleteEntry = async (entryId) => {
    if (!entryId || deletingEntryId) return;

    setDeletingEntryId(entryId);
    try {
      await axios.delete(`${API_URL}/outfit-calendar/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Removed', description: 'Outfit plan removed' });
      setShowPlanDialog(false);
      setSelectedDate(null);
      setEditingEntryId(null);
      fetchCalendarData();
    } catch (err) {
      toast({ title: '❌ Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeletingEntryId(null);
    }
  };

  // Mark as worn
  const markAsWorn = async (entryId, rating) => {
    if (!entryId || markingWornId) return;

    setMarkingWornId(entryId);
    try {
      await axios.put(`${API_URL}/outfit-calendar/${entryId}/worn`, { rating }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: '👗 Marked as worn!', description: 'Wear stats updated' });
      fetchCalendarData();
    } catch (err) {
      toast({ title: '❌ Error', description: 'Failed to update', variant: 'destructive' });
    } finally {
      setMarkingWornId(null);
    }
  };

  // Save whiteboard to Saved Items
  const saveWhiteboardToSaved = async () => {
    const name = saveOutfitName.trim();
    if (!name) {
      toast({ title: '⚠️ Name required', description: 'Please enter a name for this outfit', variant: 'destructive' });
      return;
    }

    setSavingWhiteboard(true);
    try {
      let canvasImageUrl = '';
      if (whiteboardCanvasRef.current) {
        const captured = await html2canvas(whiteboardCanvasRef.current, {
          backgroundColor: '#fafaf9',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
        });
        canvasImageUrl = captured.toDataURL('image/png');
      }

      if (!canvasImageUrl) {
        toast({ title: '❌ Error', description: 'Could not capture the whiteboard', variant: 'destructive' });
        setSavingWhiteboard(false);
        return;
      }

      await axios.post(`${API_URL}/saved`, {
        product: {
          name: name,
          brand: 'Calendar',
          image_url: canvasImageUrl,
          category: 'Outfit',
          style: 'calendar-board',
        },
        source: 'calendar-saves'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ title: '✅ Saved!', description: `"${name}" saved to your Saved Items` });
      setShowSaveNameDialog(false);
      setSaveOutfitName('');
    } catch (err) {
      toast({ title: '❌ Error', description: 'Failed to save outfit', variant: 'destructive' });
    } finally {
      setSavingWhiteboard(false);
    }
  };

  // Apply saved outfit
  const applySavedOutfit = (outfit) => {
    setPlanForm(prev => ({
      ...prev,
      top_item_id: outfit.top_item_id,
      bottom_item_id: outfit.bottom_item_id,
      dress_item_id: outfit.dress_item_id,
      outerwear_item_id: outfit.outerwear_item_id,
      shoes_item_id: outfit.shoes_item_id,
      bag_item_id: outfit.bag_item_id,
      sportswear_item_id: outfit.sportswear_item_id || null,
      swimwear_item_id: outfit.swimwear_item_id || null,
      accessory_ids: outfit.accessory_ids || [],
      outfit_combination_id: outfit.id,
      title: prev.title || outfit.name || 'Saved Outfit'
    }));
    toast({ title: '✅ Outfit applied', description: `"${outfit.name || 'Outfit'}" loaded into the planner` });
  };

  // Item picker
  const openItemPicker = (slot) => {
    setPickingSlot(slot);
    setItemSearchQuery('');
    setItemFilterCategory('all');
    setShowItemPicker(true);
  };

  const selectItem = (item) => {
    if (pickingSlot === 'accessory_multi') {
      setPlanForm(prev => ({
        ...prev,
        accessory_ids: prev.accessory_ids?.includes(item.id) ? prev.accessory_ids : [...(prev.accessory_ids || []), item.id]
      }));
      // Don't close picker so user can add multiple accessories
      return;
    }
    setPlanForm(prev => ({ ...prev, [pickingSlot]: item.id }));
    setShowItemPicker(false);
  };

  const getItemById = (id) => wardrobeItems.find(i => i.id === id);

  const getSlotLabel = (slot) => {
    const labels = {
      top_item_id: 'Top', bottom_item_id: 'Bottom', dress_item_id: 'Dress',
      outerwear_item_id: 'Outerwear', shoes_item_id: 'Shoes', bag_item_id: 'Bag',
      sportswear_item_id: 'Sportswear', swimwear_item_id: 'Swimwear'
    };
    return labels[slot] || slot;
  };

  const getSlotIcon = (slot) => {
    const icons = {
      top_item_id: '👕', bottom_item_id: '👖', dress_item_id: '👗',
      outerwear_item_id: '🧥', shoes_item_id: '👟', bag_item_id: '👜',
      sportswear_item_id: '🏃', swimwear_item_id: '👙'
    };
    return icons[slot] || '👔';
  };

  const getCategoryForSlot = (slot) => {
    const map = {
      top_item_id: 'tops', bottom_item_id: 'bottoms', dress_item_id: 'dresses',
      outerwear_item_id: 'outerwear', shoes_item_id: 'footwear', bag_item_id: 'accessories',
      sportswear_item_id: 'sportswear', swimwear_item_id: 'swimwear'
    };
    return map[slot];
  };

  const filteredPickerItems = (() => {
    const slotCat = pickingSlot === 'accessory_multi' ? 'accessories' : (pickingSlot ? getCategoryForSlot(pickingSlot) : null);
    
    const matchSearch = (item) => !itemSearchQuery ||
      (item.name || '').toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
      (item.brand || '').toLowerCase().includes(itemSearchQuery.toLowerCase());
    
    const matchCategoryStrict = (item) => {
      const cat = (item.primary_category || '').toLowerCase();
      const type = (item.clothing_type || '').toLowerCase();
      return cat === slotCat || type === slotCat || cat.includes(slotCat) || type.includes(slotCat);
    };
    
    if (itemFilterCategory !== 'all') {
      // User manually selected a filter category
      return wardrobeItems.filter(item => 
        ((item.primary_category || '').toLowerCase() === itemFilterCategory) && matchSearch(item)
      );
    }
    
    if (slotCat) {
      // Try category-specific filter first
      const catFiltered = wardrobeItems.filter(item => matchCategoryStrict(item) && matchSearch(item));
      if (catFiltered.length > 0) return catFiltered;
      // Fallback: show all items if no category match (with search filter still applied)
    }
    
    return wardrobeItems.filter(item => matchSearch(item));
  })();

  // Get week days array
  const getWeekDays = () => {
    const { start } = getWeekRange(currentDate);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  // Get month days with padding
  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    // Monday-based: getDay() returns 0 for Sunday
    const startPad = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const isToday = (date) => date && formatDateKey(date) === formatDateKey(new Date());
  const isPast = (date) => date && formatDateKey(date) < formatDateKey(new Date());

  // Header date display
  const getHeaderText = () => {
    if (viewMode === 'today') {
      return currentDate.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (viewMode === 'week') {
      const { start, end } = getWeekRange(currentDate);
      const startMonth = start.toLocaleString('default', { month: 'short' });
      const endMonth = end.toLocaleString('default', { month: 'short' });
      if (startMonth === endMonth) {
        return `${start.getDate()} – ${end.getDate()} ${endMonth} ${end.getFullYear()}`;
      }
      return `${start.getDate()} ${startMonth} – ${end.getDate()} ${endMonth} ${end.getFullYear()}`;
    }
    if (viewMode === 'year') {
      return `${currentDate.getFullYear()}`;
    }
    return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Render outfit preview for a calendar cell
  const renderOutfitPreview = (entry) => {
    if (!entry) return null;
    const items = [entry.top_item, entry.bottom_item, entry.dress_item, entry.outerwear_item, entry.shoes_item, entry.bag_item, entry.sportswear_item, entry.swimwear_item].filter(i => i && i.id);

    if (items.length === 0 && entry.custom_image_url) {
      return (
        <div className="w-full h-full rounded-lg overflow-hidden">
          <img src={entry.custom_image_url} alt="" className="w-full h-full object-cover" />
        </div>
      );
    }

    if (items.length === 0) {
      const occasion = OCCASIONS.find(o => o.value === entry.occasion);
      return (
        <div className="text-center">
          <span className="text-lg">{occasion?.emoji || '👔'}</span>
          {entry.title && <p className="text-[9px] text-stone-500 truncate mt-0.5">{entry.title}</p>}
        </div>
      );
    }

    return (
      <div className={`grid gap-0.5 w-full ${items.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'}`}>
        {items.slice(0, 4).map((item, i) => (
          <div key={i} className="aspect-square rounded overflow-hidden bg-stone-100">
            {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
          </div>
        ))}
      </div>
    );
  };

  if (!token) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-stone-700 mb-2">Log in to plan outfits</h2>
        <p className="text-stone-400">Sign in to access your Outfit Calendar</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 bg-[#faf9f7] min-h-screen">
      {/* Hero Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 pt-4"
      >
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-14 h-14 rounded-full border border-stone-300 flex items-center justify-center">
              <Calendar className="w-7 h-7 text-stone-600" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-1">PLAN YOUR LOOKS</p>
              <h1 className="text-3xl md:text-4xl font-extralight text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>Outfit Calendar</h1>
              <p className="text-xs uppercase tracking-[0.1em] text-stone-400 mt-1">Straight from your wardrobe</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <button
              onClick={goToToday}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all ${
                viewMode === 'today' ? 'bg-stone-800 text-white' : 'text-stone-500 border border-stone-200 hover:border-stone-300'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all ${
                viewMode === 'week' ? 'bg-stone-800 text-white' : 'text-stone-500 border border-stone-200 hover:border-stone-300'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all ${
                viewMode === 'month' ? 'bg-stone-800 text-white' : 'text-stone-500 border border-stone-200 hover:border-stone-300'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('year')}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all ${
                viewMode === 'year' ? 'bg-stone-800 text-white' : 'text-stone-500 border border-stone-200 hover:border-stone-300'
              }`}
            >
              Year
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={navigatePrev} className="rounded-full">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-lg font-light text-stone-800 min-w-[200px] text-center" style={{ fontFamily: 'Georgia, serif' }}>
            {getHeaderText()}
          </span>
          <Button variant="outline" size="icon" onClick={navigateNext} className="rounded-full">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-olive-500" />
        </div>
      )}

      {/* ===== TODAY VIEW ===== */}
      {!loading && viewMode === 'today' && (() => {
        const todayDate = currentDate;
        const key = formatDateKey(todayDate);
        const dateEntries = calendarEntries[key] || [];
        const dayName = todayDate.toLocaleDateString('default', { weekday: 'long' });
        const dateStr = todayDate.toLocaleDateString('default', { month: 'long', day: 'numeric', year: 'numeric' });

        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto"
          >
            <div className={`rounded-2xl border p-6 ${dateEntries.length > 0 ? 'border-amber-300 bg-white shadow-md' : 'border-stone-200 bg-white'}`}>
              <div className="text-center mb-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-1">{dayName}</p>
                <p className="text-3xl font-light text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>{todayDate.getDate()}</p>
                <p className="text-sm text-stone-500 mt-1">{dateStr}</p>
              </div>

              {dateEntries.length > 0 ? (
                <div className="space-y-4">
                  {dateEntries.map((ent, idx) => (
                    <div key={ent.id} className={`${idx > 0 ? 'pt-4 border-t border-stone-100' : ''}`}>
                      <div className="flex justify-center">
                        {renderOutfitPreview(ent)}
                      </div>
                      {ent.title && (
                        <h3 className="text-lg font-medium text-stone-800 text-center mt-2">{ent.title}</h3>
                      )}
                      {ent.occasion && (
                        <p className="text-sm text-center text-stone-500">
                          {OCCASIONS.find(o => o.value === ent.occasion)?.emoji}{' '}
                          {OCCASIONS.find(o => o.value === ent.occasion)?.label}
                        </p>
                      )}
                      {ent.weather && (
                        <p className="text-sm text-center text-stone-400">
                          {WEATHER_OPTIONS.find(w => w.value === ent.weather)?.label}
                        </p>
                      )}
                      {ent.mood && (
                        <p className="text-2xl text-center">{ent.mood}</p>
                      )}
                      {ent.notes && (
                        <p className="text-sm text-stone-500 text-center italic">{ent.notes}</p>
                      )}
                      {ent.was_worn && (
                        <div className="flex items-center justify-center gap-1">
                          <Check className="w-4 h-4 text-olive-500" />
                          <span className="text-sm text-olive-600 font-medium">Worn</span>
                        </div>
                      )}
                      <div className="flex justify-center gap-2 pt-2">
                        <Button variant="outline" onClick={() => openPlanDialog(todayDate, ent)} className="rounded-full">
                          <Eye className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        {!ent.was_worn && (
                          <Button variant="outline" onClick={() => markAsWorn(ent.id)} disabled={markingWornId === ent.id} className="rounded-full text-olive-600 hover:bg-amber-50">
                            {markingWornId === ent.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Mark Worn
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="pt-3 border-t border-stone-100">
                    <Button onClick={() => openPlanDialog(todayDate)} variant="outline" className="rounded-full w-full">
                      <Plus className="w-4 h-4 mr-1" /> Add Another Outfit
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Plus className="w-10 h-10 text-stone-200 mx-auto mb-3" />
                  <p className="text-stone-400 mb-4">No outfit planned for today</p>
                  <Button onClick={() => openPlanDialog(todayDate)} className="rounded-full bg-stone-800 hover:bg-stone-900 text-white">
                    <Plus className="w-4 h-4 mr-1" /> Plan Today's Outfit
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        );
      })()}

      {/* ===== WEEK VIEW (horizontally scrollable) ===== */}
      {!loading && viewMode === 'week' && (
        <div
          ref={weekScrollRef}
          className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            const delta = e.changedTouches[0].clientX - touchStartX.current;
            if (Math.abs(delta) > 80) {
              if (delta < 0) navigateNext();
              else navigatePrev();
            }
          }}
        >
          {getWeekDays().map((day, i) => {
            const key = formatDateKey(day);
            const dateEntries = calendarEntries[key] || [];
            const entry = dateEntries[0] || null;
            const entryCount = dateEntries.length;
            const today = isToday(day);
            const past = isPast(day);

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => openPlanDialog(day)}
                className={`flex-shrink-0 w-[140px] sm:w-auto sm:flex-1 snap-center rounded-2xl border cursor-pointer transition-all hover:shadow-md min-h-[220px] flex flex-col ${
                  today
                    ? 'border-amber-400 bg-amber-50/50 ring-2 ring-amber-300 ring-offset-1'
                    : entryCount > 0
                      ? 'border-amber-200 bg-white'
                      : 'border-stone-200 bg-white hover:border-amber-200'
                } ${past && !today ? 'opacity-75' : ''}`}
              >
                {/* Day header */}
                <div className={`px-3 py-2 border-b text-center ${today ? 'border-amber-200 bg-amber-100/50' : 'border-stone-100'}`}>
                  <p className="text-xs font-medium text-stone-400">{DAYS[i]}</p>
                  <p className={`text-lg font-bold ${today ? 'text-olive-600' : 'text-stone-800'}`}>{day.getDate()}</p>
                </div>

                {/* Content */}
                <div className="flex-1 p-2 flex flex-col items-center justify-center">
                  {entryCount > 0 ? (
                    <div className="w-full space-y-1.5">
                      {renderOutfitPreview(entry)}
                      {entry.title && (
                        <p className="text-xs font-medium text-stone-700 text-center truncate px-1">{entry.title}</p>
                      )}
                      {entryCount > 1 && (
                        <p className="text-[10px] text-center text-amber-600 font-medium">+{entryCount - 1} more</p>
                      )}
                      {entry.occasion && (
                        <p className="text-[10px] text-center text-stone-400">
                          {OCCASIONS.find(o => o.value === entry.occasion)?.emoji}{' '}
                          {OCCASIONS.find(o => o.value === entry.occasion)?.label}
                        </p>
                      )}
                      {entry.was_worn && (
                        <div className="flex items-center justify-center gap-0.5">
                          <Check className="w-3 h-3 text-olive-500" />
                          <span className="text-[10px] text-olive-600">Worn</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center">
                      <Plus className="w-6 h-6 text-stone-300 mx-auto mb-1" />
                      <p className="text-[10px] text-stone-300">Plan outfit</p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ===== MONTH VIEW ===== */}
      {!loading && viewMode === 'month' && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-stone-200">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-2 mb-3">
            {DAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-stone-500 py-2">{day}</div>
            ))}
          </div>
          {/* Month grid */}
          <div className="grid grid-cols-7 gap-2">
            {getMonthDays().map((day, i) => {
              if (!day) return <div key={i} className="aspect-square" />;
              const key = formatDateKey(day);
              const dateEntries = calendarEntries[key] || [];
              const entry = dateEntries[0] || null;
              const entryCount = dateEntries.length;
              const today = isToday(day);

              return (
                <motion.button
                  key={key}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => openPlanDialog(day)}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm transition-all p-1 ${
                    today ? 'ring-2 ring-olive-500 ring-offset-1' : ''
                  } ${entryCount > 0
                    ? 'bg-amber-100 text-amber-700 border-2 border-amber-300'
                    : 'bg-stone-50 hover:bg-amber-50 text-stone-700'
                  }`}
                >
                  <span className="font-medium text-xs">{day.getDate()}</span>
                  {entryCount > 0 && (
                    <div className="mt-0.5">
                      {entryCount > 1 ? (
                        <span className="text-[8px] font-bold text-amber-600">{entryCount}</span>
                      ) : entry.occasion ? (
                        <span className="text-xs">{OCCASIONS.find(o => o.value === entry.occasion)?.emoji || '👔'}</span>
                      ) : (
                        <Shirt className="w-3 h-3 text-olive-600" />
                      )}
                    </div>
                  )}
                  {entry?.was_worn && <Check className="w-2.5 h-2.5 text-olive-600" />}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== YEAR VIEW ===== */}
      {!loading && viewMode === 'year' && (() => {
        const year = currentDate.getFullYear();
        const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'];
        const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }, (_, monthIdx) => {
              const firstDay = new Date(year, monthIdx, 1);
              const lastDay = new Date(year, monthIdx + 1, 0);
              const startPad = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
              const daysInMonth = lastDay.getDate();

              // Count entries in this month
              const monthEntries = [];
              for (let d = 1; d <= daysInMonth; d++) {
                const key = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                if (calendarEntries[key] && calendarEntries[key].length > 0) monthEntries.push({ day: d, entry: calendarEntries[key][0] });
              }

              const isCurrentMonth = new Date().getFullYear() === year && new Date().getMonth() === monthIdx;
              const todayDate = new Date().getDate();

              return (
                <motion.div
                  key={monthIdx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: monthIdx * 0.03 }}
                  className={`bg-white rounded-2xl border p-3 cursor-pointer hover:shadow-md transition-all ${
                    isCurrentMonth ? 'border-amber-300 ring-1 ring-amber-200' : 'border-stone-200'
                  }`}
                  onClick={() => {
                    setCurrentDate(new Date(year, monthIdx, 1));
                    setViewMode('month');
                  }}
                >
                  {/* Month header */}
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-sm font-semibold ${
                      isCurrentMonth ? 'text-amber-700' : 'text-stone-700'
                    }`}>{MONTH_NAMES_SHORT[monthIdx]}</h3>
                    {monthEntries.length > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        isCurrentMonth ? 'bg-amber-100 text-amber-600' : 'bg-stone-100 text-stone-500'
                      }`}>
                        {monthEntries.length} outfit{monthEntries.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Mini month grid */}
                  <div className="grid grid-cols-7 gap-px">
                    {/* Day headers */}
                    {['M','T','W','T','F','S','S'].map((d, i) => (
                      <div key={i} className="text-center text-[8px] text-stone-300 font-medium py-0.5">{d}</div>
                    ))}
                    {/* Padding */}
                    {Array.from({ length: startPad }, (_, i) => (
                      <div key={`pad-${i}`} className="aspect-square" />
                    ))}
                    {/* Days */}
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const d = i + 1;
                      const key = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      const hasEntry = calendarEntries[key] && calendarEntries[key].length > 0;
                      const entry = hasEntry ? calendarEntries[key][0] : null;
                      const isTodayCell = isCurrentMonth && d === todayDate;

                      return (
                        <div
                          key={d}
                          className={`aspect-square rounded-sm flex items-center justify-center text-[9px] relative ${
                            isTodayCell ? 'ring-1 ring-olive-400 font-bold text-olive-700' : ''
                          } ${
                            hasEntry ? 'bg-amber-200/70 text-amber-800 font-semibold' : 'text-stone-400'
                          }`}
                          title={hasEntry ? (entry.title || `Outfit planned`) : ''}
                        >
                          {d}
                        </div>
                      );
                    })}
                  </div>

                  {/* Outfit previews for this month */}
                  {monthEntries.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-stone-100">
                      <div className="flex gap-1 overflow-hidden">
                        {monthEntries.slice(0, 4).map(({ day, entry }) => {
                          const items = [entry.top_item, entry.bottom_item, entry.dress_item, entry.shoes_item].filter(i => i && i.id);
                          const img = entry.custom_image_url || (items[0]?.image_url);
                          return (
                            <div key={day} className="flex-shrink-0">
                              {img ? (
                                <div className="w-8 h-8 rounded-md overflow-hidden bg-stone-100 border border-stone-200">
                                  <img src={img} alt="" className="w-full h-full object-cover" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 rounded-md bg-amber-100 flex items-center justify-center border border-amber-200">
                                  <span className="text-[10px]">
                                    {OCCASIONS.find(o => o.value === entry.occasion)?.emoji || '👔'}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {monthEntries.length > 4 && (
                          <div className="w-8 h-8 rounded-md bg-stone-100 flex items-center justify-center border border-stone-200">
                            <span className="text-[9px] text-stone-400 font-medium">+{monthEntries.length - 4}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        );
      })()}

      {/* ===== INLINE OUTFIT PLANNER (same page, no popup) ===== */}
      <AnimatePresence>
        {showPlanDialog && selectedDate && (
          <motion.div
            ref={plannerRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25 }}
            className="mt-6 scroll-mt-4"
          >
            <div className="bg-white rounded-2xl border border-amber-200 shadow-lg p-5 md:p-6">
              {/* Header with close */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-olive-500" />
                  <h2 className="text-lg font-medium text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>
                    {selectedDate.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h2>
                </div>
                <button
                  onClick={() => { setShowPlanDialog(false); setSelectedDate(null); setEditingEntryId(null); }}
                  className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>

              {/* Existing outfits for this day */}
              {(() => {
                const dateKey = formatDateKey(selectedDate);
                const existingEntries = calendarEntries[dateKey] || [];
                if (existingEntries.length === 0) return null;
                return (
                  <div className="mb-5 space-y-2">
                    <label className="text-sm font-medium text-stone-600 block">
                      Outfits for this day ({existingEntries.length})
                    </label>
                    <div className="space-y-3">
                      {existingEntries.map(ent => {
                        const allItems = [
                          ent.top_item, ent.bottom_item, ent.dress_item,
                          ent.outerwear_item, ent.shoes_item, ent.bag_item,
                          ent.sportswear_item, ent.swimwear_item
                        ].filter(i => i && i.id);
                        return (
                          <div key={ent.id} className={`rounded-xl border transition-all ${editingEntryId === ent.id ? 'border-amber-400 bg-amber-50' : 'border-stone-200 bg-stone-50 hover:border-amber-200'}`}>
                            {/* Entry header */}
                            <div className="flex items-center justify-between p-3 pb-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-stone-700 truncate">{ent.title || 'Untitled Outfit'}</p>
                                <p className="text-xs text-stone-400">
                                  {ent.occasion ? OCCASIONS.find(o => o.value === ent.occasion)?.emoji + ' ' + OCCASIONS.find(o => o.value === ent.occasion)?.label : 'No occasion'}
                                  {ent.was_worn ? ' • Worn ✓' : ''}
                                </p>
                              </div>
                              <div className="flex gap-1.5 flex-shrink-0">
                                <button
                                  onClick={() => { setWhiteboardEntry(ent); setShowWhiteboard(true); setItemScales({}); setItemLayers({}); setSelectedWhiteboardItem(null); }}
                                  title="View on whiteboard"
                                  className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:border-amber-300 transition-colors"
                                >
                                  <Eye className="w-3.5 h-3.5 text-stone-500" />
                                </button>
                                <button
                                  onClick={() => openPlanDialog(selectedDate, ent)}
                                  title="Edit outfit"
                                  className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:border-amber-300 transition-colors"
                                >
                                  <Pencil className="w-3.5 h-3.5 text-amber-500" />
                                </button>
                                <button
                                  onClick={() => deleteEntry(ent.id)}
                                  disabled={deletingEntryId === ent.id}
                                  title="Delete outfit"
                                  className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:border-red-300 transition-colors disabled:opacity-50"
                                >
                                  {deletingEntryId === ent.id ? <Loader2 className="w-3.5 h-3.5 text-red-400 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 text-red-400" />}
                                </button>
                              </div>
                            </div>
                            {/* Item thumbnails */}
                            {allItems.length > 0 && (
                              <div className="px-3 pb-3">
                                <div className="flex gap-1.5 overflow-x-auto">
                                  {allItems.map((item, idx) => (
                                    <div key={idx} className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-white border border-stone-100 shadow-sm">
                                      {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                                    </div>
                                  ))}
                                  {/* Show accessories too */}
                                  {ent.accessory_ids?.length > 0 && ent.accessory_ids.map(accId => {
                                    const acc = getItemById(accId);
                                    if (!acc?.image_url) return null;
                                    return (
                                      <div key={`acc-${accId}`} className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-white border border-stone-100 shadow-sm">
                                        <img src={acc.image_url} alt="" className="w-full h-full object-cover" />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {allItems.length === 0 && ent.custom_image_url && (
                              <div className="px-3 pb-3">
                                <div className="w-16 h-16 rounded-lg overflow-hidden bg-white border border-stone-100 shadow-sm">
                                  <img src={ent.custom_image_url} alt="" className="w-full h-full object-cover" />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Add new outfit button */}
                    <button
                      onClick={() => {
                        setEditingEntryId(null);
                        setPlanForm({
                          title: '', occasion: '', notes: '', weather: '', mood: '',
                          top_item_id: null, bottom_item_id: null, dress_item_id: null,
                          outerwear_item_id: null, shoes_item_id: null, bag_item_id: null,
                          sportswear_item_id: null, swimwear_item_id: null, accessory_ids: [],
                          outfit_combination_id: null, custom_image_url: ''
                        });
                        setTimeout(() => plannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 150);
                      }}
                      className="w-full rounded-xl border-2 border-dashed border-amber-300 hover:border-amber-400 hover:bg-amber-50/50 p-3 flex items-center justify-center gap-2 transition-all text-amber-600"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm font-medium">Add Another Outfit to This Day</span>
                    </button>
                    <div className="border-t border-stone-200 pt-3">
                      <p className="text-xs text-stone-400 mb-1">{editingEntryId ? 'Editing selected outfit below:' : 'Add a new outfit below:'}</p>
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-5">
                {/* Title & Occasion */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-1 block">Title</label>
                    <Input
                      value={planForm.title}
                      onChange={(e) => setPlanForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="e.g. Monday Office Look"
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-1 block">Occasion</label>
                    <Select value={planForm.occasion} onValueChange={(v) => setPlanForm(f => ({ ...f, occasion: v }))}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select occasion" /></SelectTrigger>
                      <SelectContent>
                        {OCCASIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.emoji} {o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Weather & Mood */}
                <div className="flex gap-3 flex-wrap">
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-2 block">Weather</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {WEATHER_OPTIONS.map(w => (
                        <button
                          key={w.value}
                          onClick={() => setPlanForm(f => ({ ...f, weather: f.weather === w.value ? '' : w.value }))}
                          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                            planForm.weather === w.value
                              ? 'bg-amber-100 border-2 border-amber-400'
                              : 'bg-stone-50 border border-stone-200 hover:bg-stone-100'
                          }`}
                          title={w.label}
                        >
                          <w.icon className={`w-4 h-4 ${w.color}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-2 block">Mood</label>
                    <div className="flex gap-1 flex-wrap">
                      {MOODS.map(m => (
                        <button
                          key={m}
                          onClick={() => setPlanForm(f => ({ ...f, mood: f.mood === m ? '' : m }))}
                          className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
                            planForm.mood === m ? 'bg-amber-100 border-2 border-amber-400 scale-110' : 'bg-stone-50 border border-stone-200 hover:scale-105'
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Outfit Slots */}
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Build Your Outfit</label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {['top_item_id', 'bottom_item_id', 'dress_item_id', 'outerwear_item_id', 'sportswear_item_id', 'shoes_item_id', 'swimwear_item_id', 'bag_item_id'].map(slot => {
                      const item = planForm[slot] ? getItemById(planForm[slot]) : null;
                      return (
                        <button
                          key={slot}
                          onClick={() => openItemPicker(slot)}
                          className={`relative rounded-xl border-2 border-dashed p-3 flex flex-col items-center justify-center min-h-[100px] transition-all ${
                            item ? 'border-amber-300 bg-amber-50' : 'border-stone-200 hover:border-amber-300 hover:bg-amber-50/30'
                          }`}
                        >
                          {item ? (
                            <>
                              <div className="w-14 h-14 rounded-lg overflow-hidden mb-1.5 bg-white shadow-sm">
                                <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                              </div>
                              <p className="text-[10px] text-stone-600 truncate w-full text-center">{item.name}</p>
                              <button
                                onClick={(e) => { e.stopPropagation(); setPlanForm(f => ({ ...f, [slot]: null })); }}
                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center hover:bg-red-200"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-xl mb-1">{getSlotIcon(slot)}</span>
                              <p className="text-xs text-stone-400">{getSlotLabel(slot)}</p>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Accessories — multi-select */}
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Accessories <span className="text-stone-400 font-normal">(add as many as you want)</span></label>
                  {planForm.accessory_ids?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {planForm.accessory_ids.map(accId => {
                        const accItem = getItemById(accId);
                        if (!accItem) return null;
                        return (
                          <div key={accId} className="relative group">
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-white shadow-sm border-2 border-amber-300">
                              {accItem.image_url ? (
                                <img src={accItem.image_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-stone-50">
                                  <span className="text-lg">💎</span>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => setPlanForm(f => ({ ...f, accessory_ids: f.accessory_ids.filter(id => id !== accId) }))}
                              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center hover:bg-red-200 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <p className="text-[9px] text-stone-500 truncate w-16 text-center mt-0.5">{accItem.name}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button
                    onClick={() => { setPickingSlot('accessory_multi'); setItemSearchQuery(''); setItemFilterCategory('all'); setShowItemPicker(true); }}
                    className="w-full rounded-xl border-2 border-dashed border-stone-200 hover:border-amber-300 hover:bg-amber-50/30 p-3 flex items-center justify-center gap-2 transition-all"
                  >
                    <Plus className="w-4 h-4 text-stone-400" />
                    <span className="text-xs text-stone-400">Add Accessory (jewelry, belt, hat, scarf...)</span>
                  </button>
                </div>

                {/* Saved Outfits Quick Apply */}
                {savedOutfits.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-2 block">Or use a saved outfit</label>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {savedOutfits.slice(0, 6).map(outfit => (
                        <button
                          key={outfit.id}
                          onClick={() => applySavedOutfit(outfit)}
                          className="flex-shrink-0 rounded-xl border border-stone-200 p-2 hover:border-amber-300 transition-all hover:bg-amber-50/30 min-w-[100px]"
                        >
                          {outfit.preview_image ? (
                            <img src={outfit.preview_image} alt="" className="w-16 h-16 rounded-lg object-cover mx-auto mb-1" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-stone-100 flex items-center justify-center mx-auto mb-1">
                              <Shirt className="w-6 h-6 text-stone-300" />
                            </div>
                          )}
                          <p className="text-[10px] text-stone-600 truncate text-center">{outfit.name || 'Outfit'}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Try on Whiteboard */}
                {(planForm.top_item_id || planForm.bottom_item_id || planForm.dress_item_id || planForm.outerwear_item_id || planForm.shoes_item_id || planForm.bag_item_id || planForm.sportswear_item_id || planForm.swimwear_item_id || (planForm.accessory_ids && planForm.accessory_ids.length > 0)) && (
                  <div>
                    <Button
                      variant="outline"
                      onClick={() => { setWhiteboardEntry(null); setShowWhiteboard(true); setItemScales({}); setItemLayers({}); setSelectedWhiteboardItem(null); }}
                      className="w-full rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 py-3"
                    >
                      <Sparkles className="w-4 h-4 mr-2" /> Try it on the Whiteboard
                    </Button>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-1 block">Notes</label>
                  <Textarea
                    value={planForm.notes}
                    onChange={(e) => setPlanForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Meeting with client, dinner afterwards..."
                    className="rounded-xl resize-none"
                    rows={2}
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-3 pt-2">
                  <div className="flex gap-2 flex-wrap">
                    {editingEntryId && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => deleteEntry(editingEntryId)}
                          disabled={deletingEntryId === editingEntryId}
                          className="rounded-xl text-red-500 hover:bg-red-50 hover:text-red-600 flex-1 sm:flex-none"
                        >
                          {deletingEntryId === editingEntryId ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Remove
                        </Button>
                        {!((calendarEntries[selectedDate ? formatDateKey(selectedDate) : ''] || []).find(e => e.id === editingEntryId)?.was_worn) && (
                          <Button
                            variant="outline"
                            onClick={() => markAsWorn(editingEntryId)}
                            disabled={markingWornId === editingEntryId}
                            className="rounded-xl text-olive-600 hover:bg-amber-50 flex-1 sm:flex-none"
                          >
                            {markingWornId === editingEntryId ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Mark Worn
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  <Button
                    onClick={savePlan}
                    disabled={saving}
                    className="rounded-full bg-stone-800 hover:bg-stone-900 text-white w-full sm:w-auto"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                    Save Plan
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== WHITEBOARD PREVIEW ===== */}
      <AnimatePresence>
        {showWhiteboard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-2 md:p-4"
            onClick={() => { setShowWhiteboard(false); setWhiteboardEntry(null); setItemScales({}); setItemLayers({}); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl md:rounded-3xl max-w-2xl w-full max-h-[95vh] overflow-auto flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-stone-100 p-3 md:p-4 flex items-center justify-between z-10 flex-shrink-0">
                <div className="min-w-0 flex-1 pr-2">
                  <h2 className="text-base md:text-xl font-bold text-stone-800 truncate">{whiteboardEntry?.title || planForm.title || 'Outfit Preview'}</h2>
                  <p className="text-xs md:text-sm text-stone-500">Tap to select · Then drag to move · Pinch to resize</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setShowWhiteboard(false); setWhiteboardEntry(null); setItemScales({}); setItemLayers({}); setSelectedWhiteboardItem(null); }}>
                  <X className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </div>

              {/* Draggable Canvas */}
              <div className="p-3 md:p-6 bg-white overflow-auto flex-1">
                <div ref={whiteboardCanvasRef} className="relative z-0 mx-auto bg-stone-50 rounded-xl md:rounded-2xl border-2 border-dashed border-stone-200 overflow-hidden" style={{ maxWidth: '450px', height: 'min(520px, 60vh)' }} onClick={() => setSelectedWhiteboardItem(null)}>
                  {(() => {
                    const previewItems = [];
                    // Use whiteboardEntry (saved entry) or planForm (current form)
                    if (whiteboardEntry) {
                      // From a saved calendar entry — use the joined item objects
                      if (whiteboardEntry.outerwear_item?.image_url) previewItems.push({ key: 'outerwear', src: whiteboardEntry.outerwear_item.image_url, label: '🧥 Outerwear', initX: 10, initY: 10, w: 130, h: 150 });
                      if (whiteboardEntry.top_item?.image_url) previewItems.push({ key: 'top', src: whiteboardEntry.top_item.image_url, label: '👕 Top', initX: 140, initY: 20, w: 160, h: 190 });
                      if (whiteboardEntry.dress_item?.image_url && !whiteboardEntry.top_item?.id) previewItems.push({ key: 'dress', src: whiteboardEntry.dress_item.image_url, label: '👗 Dress', initX: 140, initY: 20, w: 160, h: 240 });
                      if (whiteboardEntry.bottom_item?.image_url) previewItems.push({ key: 'bottom', src: whiteboardEntry.bottom_item.image_url, label: '👖 Bottom', initX: 140, initY: 220, w: 150, h: 180 });
                      if (whiteboardEntry.sportswear_item?.image_url) previewItems.push({ key: 'sportswear', src: whiteboardEntry.sportswear_item.image_url, label: '🏃 Sportswear', initX: 10, initY: 170, w: 130, h: 150 });
                      if (whiteboardEntry.swimwear_item?.image_url) previewItems.push({ key: 'swimwear', src: whiteboardEntry.swimwear_item.image_url, label: '👙 Swimwear', initX: 10, initY: 330, w: 120, h: 130 });
                      if (whiteboardEntry.shoes_item?.image_url) previewItems.push({ key: 'shoes', src: whiteboardEntry.shoes_item.image_url, label: '👟 Shoes', initX: 170, initY: 410, w: 110, h: 90 });
                      if (whiteboardEntry.bag_item?.image_url) previewItems.push({ key: 'bag', src: whiteboardEntry.bag_item.image_url, label: '👜 Bag', initX: 330, initY: 160, w: 100, h: 120 });
                      if (whiteboardEntry.accessory_ids?.length > 0) {
                        whiteboardEntry.accessory_ids.forEach((accId, idx) => {
                          const acc = getItemById(accId);
                          if (acc?.image_url) previewItems.push({ key: `acc-${accId}`, src: acc.image_url, label: `✨ ${acc.name || 'Accessory'}`, initX: 320, initY: 20 + idx * 70, w: 80, h: 80 });
                        });
                      }
                    } else {
                      // From the plan form — use item IDs and look them up
                      if (planForm.outerwear_item_id) { const it = getItemById(planForm.outerwear_item_id); if (it?.image_url) previewItems.push({ key: 'outerwear', src: it.image_url, label: '🧥 Outerwear', initX: 10, initY: 10, w: 130, h: 150 }); }
                      if (planForm.top_item_id) { const it = getItemById(planForm.top_item_id); if (it?.image_url) previewItems.push({ key: 'top', src: it.image_url, label: '👕 Top', initX: 140, initY: 20, w: 160, h: 190 }); }
                      if (planForm.dress_item_id && !planForm.top_item_id) { const it = getItemById(planForm.dress_item_id); if (it?.image_url) previewItems.push({ key: 'dress', src: it.image_url, label: '👗 Dress', initX: 140, initY: 20, w: 160, h: 240 }); }
                      if (planForm.bottom_item_id) { const it = getItemById(planForm.bottom_item_id); if (it?.image_url) previewItems.push({ key: 'bottom', src: it.image_url, label: '👖 Bottom', initX: 140, initY: 220, w: 150, h: 180 }); }
                      if (planForm.sportswear_item_id) { const it = getItemById(planForm.sportswear_item_id); if (it?.image_url) previewItems.push({ key: 'sportswear', src: it.image_url, label: '🏃 Sportswear', initX: 10, initY: 170, w: 130, h: 150 }); }
                      if (planForm.swimwear_item_id) { const it = getItemById(planForm.swimwear_item_id); if (it?.image_url) previewItems.push({ key: 'swimwear', src: it.image_url, label: '👙 Swimwear', initX: 10, initY: 330, w: 120, h: 130 }); }
                      if (planForm.shoes_item_id) { const it = getItemById(planForm.shoes_item_id); if (it?.image_url) previewItems.push({ key: 'shoes', src: it.image_url, label: '👟 Shoes', initX: 170, initY: 410, w: 110, h: 90 }); }
                      if (planForm.bag_item_id) { const it = getItemById(planForm.bag_item_id); if (it?.image_url) previewItems.push({ key: 'bag', src: it.image_url, label: '👜 Bag', initX: 330, initY: 160, w: 100, h: 120 }); }
                      if (planForm.accessory_ids?.length > 0) {
                        planForm.accessory_ids.forEach((accId, idx) => {
                          const acc = getItemById(accId);
                          if (acc?.image_url) previewItems.push({ key: `acc-${accId}`, src: acc.image_url, label: `✨ ${acc.name || 'Accessory'}`, initX: 320, initY: 20 + idx * 70, w: 80, h: 80 });
                        });
                      }
                    }

                    if (previewItems.length === 0) {
                      return (
                        <div className="absolute inset-0 flex items-center justify-center text-stone-300">
                          <div className="text-center">
                            <Shirt className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No items selected</p>
                          </div>
                        </div>
                      );
                    }

                    // Store item keys for layer controls
                    whiteboardItemKeysRef.current = previewItems.map((p, i) => ({ key: p.key, defaultZ: 10 + i }));

                    return previewItems.map((item, idx) => {
                      const scale = itemScales[item.key] || 1;
                      const isSelected = selectedWhiteboardItem === item.key;
                      const layerZ = itemLayers[item.key] ?? (10 + idx);
                      return (
                        <motion.div
                          key={item.key}
                          drag
                          dragListener={isSelected}
                          dragMomentum={false}
                          dragElastic={0}
                          initial={{ x: item.initX, y: item.initY, opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.08 }}
                          style={{
                            width: item.w * scale,
                            height: item.h * scale,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: layerZ,
                            touchAction: 'none',
                          }}
                          whileDrag={{ scale: 1.05 }}
                          className={isSelected ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
                          onDragStart={() => {
                            dragFlagRef.current = true;
                          }}
                          onDragEnd={() => {
                            setTimeout(() => { dragFlagRef.current = false; }, 100);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (dragFlagRef.current) return;
                            setSelectedWhiteboardItem(item.key);
                          }}
                          onTouchStart={(e) => {
                            if (e.touches.length === 2 && isSelected) {
                              const dist = Math.hypot(
                                e.touches[0].clientX - e.touches[1].clientX,
                                e.touches[0].clientY - e.touches[1].clientY
                              );
                              e.currentTarget._pinchStart = dist;
                              e.currentTarget._pinchBaseScale = itemScales[item.key] || 1;
                            }
                          }}
                          onTouchMove={(e) => {
                            if (e.touches.length === 2 && e.currentTarget._pinchStart && isSelected) {
                              e.preventDefault();
                              const dist = Math.hypot(
                                e.touches[0].clientX - e.touches[1].clientX,
                                e.touches[0].clientY - e.touches[1].clientY
                              );
                              const ratio = dist / e.currentTarget._pinchStart;
                              const newScale = Math.max(0.3, Math.min(3, e.currentTarget._pinchBaseScale * ratio));
                              setItemScales(prev => ({ ...prev, [item.key]: newScale }));
                            }
                          }}
                          onTouchEnd={(e) => {
                            if (e.touches.length < 2) {
                              e.currentTarget._pinchStart = null;
                              e.currentTarget._pinchBaseScale = null;
                            }
                          }}
                        >
                          {isSelected && (
                            <div className="absolute pointer-events-none" style={{
                              top: '10%',
                              left: '10%',
                              right: '10%',
                              bottom: '10%',
                              borderRadius: '16px',
                              background: 'radial-gradient(ellipse at center, rgba(148, 100, 52, 0.7) 0%, rgba(148, 100, 52, 0.45) 35%, rgba(148, 100, 52, 0.2) 65%, transparent 100%)',
                              filter: 'blur(8px)',
                              zIndex: -1,
                            }} />
                          )}
                          <img
                            src={item.src}
                            alt={item.label}
                            className="w-full h-full object-contain drop-shadow-lg select-none pointer-events-none"
                            draggable={false}
                          />
                        </motion.div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Layer controls — visible when an item is selected */}
              {selectedWhiteboardItem && (
                <div className="flex items-center justify-center gap-3 px-3 py-2 bg-stone-50 border-t border-stone-100">
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 shadow-sm text-xs font-medium text-stone-600 active:bg-stone-100 transition-colors"
                    onClick={() => {
                      setItemLayers(prev => {
                        // Get effective z for every item (explicit or default)
                        const allZ = whiteboardItemKeysRef.current.map(({ key, defaultZ }) => prev[key] ?? defaultZ);
                        const maxZ = Math.max(...allZ, 10);
                        return { ...prev, [selectedWhiteboardItem]: maxZ + 5 };
                      });
                    }}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                    Bring Front
                  </button>
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 shadow-sm text-xs font-medium text-stone-600 active:bg-stone-100 transition-colors"
                    onClick={() => {
                      setItemLayers(prev => {
                        // Get effective z for every item (explicit or default)
                        const allZ = whiteboardItemKeysRef.current.map(({ key, defaultZ }) => prev[key] ?? defaultZ);
                        const minZ = Math.min(...allZ, 10);
                        return { ...prev, [selectedWhiteboardItem]: Math.max(1, minZ - 5) };
                      });
                    }}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                    Send Back
                  </button>
                </div>
              )}

              {/* Footer */}
              <div className="sticky bottom-0 z-10 bg-white/95 backdrop-blur-md border-t border-stone-100 p-3 md:p-4 flex gap-2 md:gap-3 flex-shrink-0">
                <Button
                  variant="outline"
                  className="rounded-xl text-sm md:text-base py-2 md:py-2.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => {
                    setSaveOutfitName(whiteboardEntry?.title || planForm.title || '');
                    setShowSaveNameDialog(true);
                  }}
                >
                  <Bookmark className="w-4 h-4 mr-1.5" />
                  Save
                </Button>
                <Button
                  className="flex-1 bg-olive-600 hover:bg-olive-700 text-white rounded-xl text-sm md:text-base py-2 md:py-2.5"
                  onClick={() => { setShowWhiteboard(false); setWhiteboardEntry(null); setItemScales({}); setItemLayers({}); setSelectedWhiteboardItem(null); }}
                >
                  Done
                </Button>
              </div>

              {/* Save Name Dialog */}
              <AnimatePresence>
                {showSaveNameDialog && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/40 z-20 flex items-center justify-center p-4"
                    onClick={() => setShowSaveNameDialog(false)}
                  >
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.9, opacity: 0 }}
                      className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl"
                      onClick={e => e.stopPropagation()}
                    >
                      <h3 className="text-lg font-bold text-stone-800 mb-1">Save Outfit</h3>
                      <p className="text-xs text-stone-400 mb-4">This will be saved to your Saved Items under Calendar Saves</p>
                      <Input
                        value={saveOutfitName}
                        onChange={(e) => setSaveOutfitName(e.target.value)}
                        placeholder="Name your outfit..."
                        className="rounded-xl mb-4"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1 rounded-xl"
                          onClick={() => setShowSaveNameDialog(false)}
                          disabled={savingWhiteboard}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
                          onClick={saveWhiteboardToSaved}
                          disabled={savingWhiteboard || !saveOutfitName.trim()}
                        >
                          {savingWhiteboard ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Bookmark className="w-4 h-4 mr-1.5" />}
                          {savingWhiteboard ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== ITEM PICKER DIALOG ===== */}
      <Dialog open={showItemPicker} onOpenChange={setShowItemPicker}>
        <DialogContent className="sm:max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-xl">{pickingSlot === 'accessory_multi' ? '💎' : (pickingSlot ? getSlotIcon(pickingSlot) : '👔')}</span>
              {pickingSlot === 'accessory_multi' ? 'Add Accessories' : `Choose ${pickingSlot ? getSlotLabel(pickingSlot) : 'Item'}`}
            </DialogTitle>
            {pickingSlot === 'accessory_multi' && (
              <p className="text-xs text-stone-400">Tap items to add — add as many as you like! Close when done.</p>
            )}
          </DialogHeader>

          <div className="space-y-3">
            <Input
              value={itemSearchQuery}
              onChange={(e) => setItemSearchQuery(e.target.value)}
              placeholder="Search your wardrobe..."
              className="rounded-xl"
            />

            <div className="max-h-[50vh] overflow-y-auto space-y-1">
              {filteredPickerItems.length === 0 ? (
                <div className="text-center py-8">
                  <Shirt className="w-10 h-10 text-stone-200 mx-auto mb-2" />
                  <p className="text-sm text-stone-400">No items found</p>
                </div>
              ) : (
                filteredPickerItems.map(item => {
                  const isAccSelected = pickingSlot === 'accessory_multi' && planForm.accessory_ids?.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => selectItem(item)}
                      className={`w-full flex items-center gap-3 p-2 rounded-xl transition-colors text-left ${
                        isAccSelected ? 'bg-amber-100 border border-amber-300' : 'hover:bg-amber-50'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0 relative">
                        {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                        {isAccSelected && (
                          <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                            <Check className="w-5 h-5 text-amber-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-700 truncate">{item.name}</p>
                        <p className="text-xs text-stone-400">{item.brand || item.primary_category || 'Wardrobe item'}</p>
                      </div>
                      {isAccSelected && <span className="text-xs font-medium text-amber-600">Added ✓</span>}
                      {item.color && !isAccSelected && (
                        <div
                          className="w-4 h-4 rounded-full border border-stone-200"
                          style={{ backgroundColor: item.color.toLowerCase() }}
                        />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
